/**
 * Progress (B-15) — the score over time, at four granularities.
 *
 * The aggregation is `packages/core`'s `aggregate()`, unchanged: This month and 3M are
 * one bar per gym day, 6M weekly totals, All monthly totals as a line. `withGaps` then
 * gives every missed day, week or month its slot on the axis (B-58, D-109), drawn as a
 * short `--edge` stub — a gap in training reads as a run of stubs, and a bar's distance
 * from its neighbour is the calendar's distance.
 *
 * One series, so no legend — the heading names it. Scores are recomputed inside
 * `aggregate` rather than read from the stored field (D-36), so the chart and the ledger
 * beneath it — which recomputes independently in `DayRow` — cannot disagree.
 *
 * Two things the audit found. A partial last bucket used to slide toward zero and read
 * as "stopped training" — it is now drawn hollow, so the shape says what the footnote
 * says. And the space under the chart, previously about 40% of the viewport left empty,
 * now carries the summary a chart cannot state: the best session and the average.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { ChartPoint, ChartRange } from "@altrtrack/core";
import { aggregate, bucketKey, formatNumber, plural, scoreWorkout, withGaps, yesterday } from "@altrtrack/core";
import { daysInRange } from "../buckets.js";
import { DayRow } from "../components/DayRow.js";
import { localToday, useProfile, useWorkouts } from "../data.js";

const RANGES: { value: ChartRange; label: string; unit: string; empty: string }[] = [
  { value: "month", label: "This month", unit: "one bar per day", empty: "this month" },
  { value: "3M", label: "3 months", unit: "one bar per day", empty: "in the last 3 months" },
  { value: "6M", label: "6 months", unit: "weekly totals", empty: "in the last 6 months" },
  { value: "all", label: "All", unit: "monthly totals", empty: "yet" },
];

export function ProgressPage({ uid }: { uid: string }) {
  const navigate = useNavigate();
  const workouts = useWorkouts(uid);
  const profile = useProfile(uid);
  const [range, setRange] = useState<ChartRange>("month");
  /*
   * The bucket whose days are highlighted in the ledger below, or null.
   *
   * Only 6M and All ever set it: a bar that is one gym day opens that day's editor
   * instead. The drill-down panel this replaces listed those days itself — with the
   * ledger on the same screen that was two lists of the same days on one page, which is
   * the duplication D-09 exists to prevent (D-86, superseding half of D-82).
   */
  const [lit, setLit] = useState<string | null>(null);
  const ledgerRef = useRef<HTMLElement | null>(null);
  const [openDate, setOpenDate] = useState<string | null>(null);

  const today = localToday(profile.data?.timezone ?? "UTC");
  const points = useMemo(
    () => (workouts.data ? withGaps(aggregate(workouts.data, range, today), range, today) : []),
    [workouts.data, range, today],
  );
  const ledger = useMemo(
    () => (workouts.data ? daysInRange(workouts.data, range, today) : []),
    [workouts.data, range, today],
  );

  /*
   * Scrolling to the highlighted week or month.
   *
   * `pick` only sets `lit` — it cannot scroll itself, because the rows it would scroll to
   * are rendered from that same state and do not exist yet in the click handler that sets
   * it. This effect runs after the re-render that puts them on screen, and scrolls to the
   * first one rather than to the top of the ledger container, which is off-screen by
   * thousands of pixels at 6M or All with a long history.
   *
   * Above every early return, with the rest of the hooks — a hook placed below one
   * typechecks cleanly and only fails at runtime (D-56, three times over in this repo).
   */
  useEffect(() => {
    if (!lit) return;
    ledgerRef.current?.querySelector(".day.lit")?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [lit]);

  if (workouts.loading) return <p className="sub">Loading…</p>;

  const total = points.reduce((sum, p) => sum + p.score, 0);
  const days = points.reduce((sum, p) => sum + p.dayCount, 0);
  // Only whole days are "missed" (B-58): an empty week or month may be the current one.
  const missed = range === "month" || range === "3M" ? points.filter((p) => p.dayCount === 0).length : 0;
  const active = RANGES.find((r) => r.value === range)!;
  const best = points.reduce((m, p) => Math.max(m, p.score), 0);
  // A single-day bucket — This month or 3M — is already one gym day, so it can never be
  // partial, is never a drill-down target, and its tooltip never needs a day count.
  const singleDay = range === "month" || range === "3M";
  // A monthly or weekly total for the period you are standing in is always short, and
  // the last point sliding toward zero reads as "stopped training" rather than "it is
  // the 4th". Saying so is cheaper than truncating the range.
  const partial =
    !singleDay && points.length > 0 && points[points.length - 1]!.bucket <= today &&
    bucketContains(points[points.length - 1]!.bucket, today, range);
  const lastIndex = points.length - 1;
  // The bar on each row is sized against the best day in the ledger, not the best bucket
  // in the chart — a weekly total would make every individual day look tiny.
  const ledgerBest = Math.max(...ledger.map((w) => scoreWorkout(w)), 1);

  /*
   * Splitting the line into a solid run and a dashed tail. A hollow last dot was not
   * enough on its own: the *line* still plunged to near-zero, which is the misreading
   * the footnote exists to correct. Two series over the same data — `full` stops one
   * point early, `tail` covers only the final segment — so the shape says "unfinished"
   * without the footnote having to.
   */
  /*
   * Tapping a column.
   *
   * At a single-day range a column is one gym day, so it goes to that day's editor. At 6M
   * and All it is a week or a month, so instead the ledger below is scrolled to and the
   * days it stands for are highlighted there — a bucket is not a date.
   *
   * The bucket comes from the datum recharts hands the handler, never from the chart's
   * own `activeLabel`. That state is one interaction stale at click time — a tap opened
   * the bar tapped before it, and the first tap of all opened nothing. It looked right in
   * the code and was wrong on the screen, which is D-56 in the browser again.
   */
  const pick = (point: { bucket?: string; dayCount?: number } | undefined) => {
    const bucket = point?.bucket;
    if (!bucket) return;
    if (singleDay) {
      // A missed day opens the editor too: /day/:date creates the day when it has none,
      // which is how a session that was done but never logged gets back in.
      void navigate(`/day/${bucket}`, { state: { from: "progress" } });
      return;
    }
    // A missed week or month has no rows in the ledger to highlight.
    if (point.dayCount === 0) return;
    setLit(lit === bucket ? null : bucket);
  };

  const series = points.map((p, i) => ({
    ...p,
    full: partial && i === lastIndex ? null : p.score,
    tail: partial && i >= lastIndex - 1 ? p.score : null,
  }));

  return (
    <>
      <div className="headline">
        {/*
          The range picker IS the heading above the total (B-40). It was a labelled select
          in a row of its own, which spent a whole band of the screen restating a word the
          chart already says. Still a native select, so tapping it opens the system picker
          with no popover code of our own, it is reachable by keyboard, and the current
          range reads as the heading it always was.

          It carries `.select`'s --t-control (16px) rather than the label tier's 15px:
          iOS zooms any focused control under 16px and does not zoom back out (D-64). The
          caret is the only thing that says it can be tapped, so it is not optional.
        */}
        <select
          id="range"
          className="select range-heading"
          aria-label="Showing"
          value={range}
          onChange={(e) => {
            // A bucket key means nothing at another granularity — "2026-08" is not a
            // week — so anything highlighted is cleared rather than carried across.
            setLit(null);
            setRange(e.target.value as ChartRange);
          }}
        >
          {RANGES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
        <p className="headline-score num">{formatNumber(total)}</p>
        <p className="sub">
          across {days} gym {plural(days, "day")}
          {missed > 0 && ` · ${missed} missed`} · {active.unit}
        </p>
      </div>

      {points.length === 0 ? (
        <>
          <p className="sub">
            Nothing logged {active.empty}.{" "}
            {(workouts.data?.length ?? 0) > 0
              ? "Pick a longer range to see earlier days."
              : "Message the bot a set — bench 60x10x3 — and it will appear here."}
          </p>
          {/*
            An empty range draws no axis, so there is no missed day to tap (B-61). This is
            the same editor a tapped day opens; it starts on yesterday and its date field
            walks back to any earlier day.
          */}
          <div className="actions">
            <Link className="btn" to={`/day/${yesterday(today)}`} state={{ from: "progress" }}>
              Log a day you missed
            </Link>
          </div>
        </>
      ) : (
        <>
          <div className="chart">
            <ResponsiveContainer width="100%" height={130}>
              {range === "all" ? (
                /*
                  A composed chart rather than a line chart, for the invisible bar below:
                  the months are drawn as a line, but a month still has to be tappable, and
                  a 12px dot is not a target on a phone.
                */
                <ComposedChart data={series} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                  {axes(range)}
                  <Bar
                    dataKey="score"
                    fill="transparent"
                    isAnimationActive={false}
                    style={{ cursor: "pointer" }}
                    background={{ fill: "transparent" }}
                    onClick={(entry: BarClick) => pick(entry?.payload)}
                  />
                  <Line
                    type="monotone"
                    dataKey="full"
                    stroke="var(--live)"
                    strokeWidth={2}
                    connectNulls={false}
                    dot={({ cx, cy, index }: DotProps) => (
                      <PointDot cx={cx} cy={cy} hollow={false} missed={series[index ?? -1]?.dayCount === 0} />
                    )}
                    activeDot={{ r: 6 }}
                    isAnimationActive={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="tail"
                    stroke="var(--live)"
                    strokeWidth={2}
                    strokeDasharray="4 3"
                    connectNulls={false}
                    dot={({ cx, cy, index }: DotProps) => (
                      <PointDot cx={cx} cy={cy} hollow={index === lastIndex} />
                    )}
                    activeDot={{ r: 6 }}
                    isAnimationActive={false}
                  />
                </ComposedChart>
              ) : (
                <BarChart data={points} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                  {axes(range)}
                  {/*
                    `background` draws a transparent rectangle the full height of the plot
                    behind each bar, and recharts gives those rectangles the Bar's own
                    handlers. That is the touch target: a weekly bar is about ten pixels
                    wide and sometimes three tall, which is not one.
                  */}
                  {/*
                    `minPointSize` is what draws a missed bucket at all: a zero bar is
                    otherwise no bar, and the gap would be indistinguishable from the
                    plot's own padding. Three pixels is a stub, not a value — the tooltip
                    says "Missed", and the colour is `--edge`, not `--live`.
                  */}
                  <Bar
                    dataKey="score"
                    radius={[3, 3, 0, 0]}
                    maxBarSize={26}
                    minPointSize={3}
                    isAnimationActive={false}
                    style={{ cursor: "pointer" }}
                    background={{ fill: "transparent" }}
                    onClick={(entry: BarClick) => pick(entry?.payload)}
                  >
                    {points.map((p, i) =>
                      partial && i === lastIndex ? (
                        // The bucket you are standing in is always short. Drawn hollow so
                        // the shape says "unfinished" rather than "stopped training".
                        <Cell
                          key={p.bucket}
                          fill="transparent"
                          stroke="var(--live)"
                          strokeWidth={1.5}
                          strokeDasharray="3 2"
                        />
                      ) : p.dayCount === 0 ? (
                        // Missed: a stub in the recessive token, so it marks the slot
                        // without competing with the bars that are the data.
                        <Cell key={p.bucket} fill="var(--edge)" fillOpacity={lit ? 0.35 : 1} />
                      ) : (
                        <Cell
                          key={p.bucket}
                          fill="var(--live)"
                          fillOpacity={lit && lit !== p.bucket ? 0.35 : 1}
                        />
                      ),
                    )}
                  </Bar>
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>

          <div className="chart-stats">
            <div className="stat">
              <span className="label">Best {singleDay ? "day" : "bucket"}</span>
              <span className="stat-value num">{formatNumber(best)}</span>
            </div>
            <div className="stat">
              <span className="label">Avg. per gym day</span>
              <span className="stat-value num">
                {formatNumber(days > 0 ? Math.round(total / days) : 0)}
              </span>
            </div>
          </div>

          {/*
            Only the bucket ranges explain themselves. "Tap a bar to open that day" was
            removed on request: at a day-per-bar range the bars ARE the days listed
            directly below, and the instruction sat between them saying so.
          */}
          {!lit && !singleDay && (
            <p className="sub chart-note">
              Tap a {range === "6M" ? "week" : "month"} to highlight those days below.
            </p>
          )}

          {partial && (
            <p className="sub chart-note">
              The outlined {range === "6M" ? "week" : "month"} is still in progress, so its
              total is not finished yet.
            </p>
          )}

          {/*
            The ledger. Deliberately not an editor: correcting a day happens at
            /day/:date and nowhere else, and a second set of set rows here would be D-09
            on the screen where it would be least visible.
          */}
          <section
            className={`ledger${lit ? " has-lit" : ""}`}
            ref={ledgerRef}
            aria-label={`Gym days · ${active.label}`}
          >
            <h2 className="label ledger-head">
              {active.label} · {ledger.length} gym {plural(ledger.length, "day")}
            </h2>
            <ol className="days">
              {ledger.map((workout) => {
                const score = scoreWorkout(workout);
                return (
                  <DayRow
                    key={workout.date}
                    workout={workout}
                    score={score}
                    share={score / ledgerBest}
                    today={today}
                    open={openDate === workout.date}
                    onToggle={() =>
                      setOpenDate(openDate === workout.date ? null : workout.date)
                    }
                    lit={lit !== null && bucketKey(workout.date, range) === lit}
                  />
                );
              })}
            </ol>
          </section>
        </>
      )}
    </>
  );
}

/** What recharts hands a Bar's click handler: the row it was drawn from. */
type BarClick = { payload?: { bucket?: string; dayCount?: number } } | undefined;

type DotProps = { cx?: number | undefined; cy?: number | undefined; index?: number | undefined };

/**
 * The last point of a range you are standing in is a partial total. On the bar charts it
 * is drawn hollow; the line chart has to say the same thing or the footnote below is
 * describing something that is not on screen.
 */
function PointDot({
  cx,
  cy,
  hollow,
  missed = false,
}: {
  cx?: number | undefined;
  cy?: number | undefined;
  hollow: boolean;
  /** A month with no gym day in it (B-58): marked in `--edge`, like the bars' stubs. */
  missed?: boolean;
}) {
  // A null in the series renders no dot at all rather than one at the origin.
  if (cx === undefined || cy === undefined || Number.isNaN(cy)) return null;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={4}
      fill={hollow ? "var(--base)" : missed ? "var(--edge)" : "var(--live)"}
      stroke={hollow ? "var(--live)" : "var(--base)"}
      strokeWidth={2}
      {...(hollow ? { strokeDasharray: "3 2" } : {})}
    />
  );
}

/** True when `today` falls inside the bucket, i.e. the bucket is still filling up. */
function bucketContains(bucket: string, today: string, range: ChartRange): boolean {
  if (range === "all") return bucket === today.slice(0, 7);
  // 6M buckets are Monday-named; today is in it if it is within the next six days.
  const start = Date.parse(`${bucket}T00:00:00Z`);
  const now = Date.parse(`${today}T00:00:00Z`);
  return now >= start && now - start < 7 * 86_400_000;
}

/** Shared axis furniture. Recessive: hairline grid, no axis lines, muted ticks. */
function axes(range: ChartRange) {
  return [
    <CartesianGrid
      key="grid"
      stroke="var(--edge)"
      strokeOpacity={0.35}
      strokeDasharray="0"
      vertical={false}
    />,
    <XAxis
      key="x"
      dataKey="bucket"
      tickFormatter={(bucket: string) => bucketLabel(bucket, range)}
      tick={{ fill: "var(--dim)", fontSize: 12 }}
      axisLine={false}
      tickLine={false}
      minTickGap={24}
    />,
    <YAxis
      key="y"
      tick={{ fill: "var(--dim)", fontSize: 12 }}
      axisLine={false}
      tickLine={false}
      width={44}
      tickCount={4}
      tickFormatter={(v: number) => (v >= 1000 ? `${Math.round(v / 1000)}k` : String(v))}
    />,
    <Tooltip
      key="tip"
      cursor={{ fill: "var(--edge)", fillOpacity: 0.25 }}
      content={<PointTooltip range={range} />}
    />,
  ];
}

function PointTooltip({
  active,
  payload,
  range,
}: {
  active?: boolean;
  payload?: { payload: ChartPoint }[];
  range?: ChartRange;
}) {
  const point = payload?.[0]?.payload;
  if (!active || !point || !range) return null;

  return (
    <div className="tooltip">
      <div className="tooltip-when">{bucketLabel(point.bucket, range, true)}</div>
      {point.dayCount === 0 ? (
        // "Missed" only for a whole day. An empty week or month may be the one you are
        // standing in, which is not missed yet.
        <div className="sub">{range === "3M" || range === "month" ? "Missed" : "No gym days"}</div>
      ) : (
        <div className="tooltip-score num">{formatNumber(point.score)}</div>
      )}
      {range !== "3M" && range !== "month" && point.dayCount > 0 && (
        <div className="sub">
          {point.dayCount} gym {plural(point.dayCount, "day")}
        </div>
      )}
    </div>
  );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "12 Sep", "week of 12 Sep", "Sep 2026" — whichever the bucket actually is. */
function bucketLabel(bucket: string, range: ChartRange, long = false): string {
  const [y, m, d] = bucket.split("-").map(Number) as [number, number, number?];
  const month = MONTHS[m - 1] ?? "";
  if (range === "all") return long ? `${month} ${y}` : `${month} ${String(y).slice(2)}`;
  const day = `${d} ${month}`;
  return range === "6M" && long ? `Week of ${day}` : day;
}
